/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet';

import { useRetracked } from 'js/lib/retracked';

import { useTracker } from '@coursera/event-pulse/react';

import _t from 'i18n!nls/ent-website';

type Props = {
  url: string;
};

function getPdfObjectFallbackStrings() {
  return {
    text: _t('This browser does not support inline PDFs. Please download the PDF to view it: '),
    link: _t('Download PDF'),
  };
}

function PDFObjectRenderer({ url }: Props) {
  const track = useRetracked();
  const trackV3 = useTracker();
  const styles = {
    root: css`
      .pdfobject-container {
        height: 80vh;
      }
    `,
    pdfContainer: css`
      width: 100%;
    `,
  };

  const [reload, setReload] = React.useState(false);

  React.useEffect(() => {
    // Do not load on SSR
    if (typeof window === 'undefined') {
      return;
    }

    const PDFObject = window.PDFObject;

    if (typeof PDFObject === 'undefined') {
      // recheck after 100ms
      window.setTimeout(() => setReload(!reload), 100);
      return;
    }

    const el = PDFObject.embed(url, '#pdf-object', {
      // Any string entered here will be inserted into the target element when the browser doesn't support inline PDFs
      fallbackLink: `<p>${getPdfObjectFallbackStrings().text}<a class='download-link' target='_blank' href='[url]'>${
        getPdfObjectFallbackStrings().link
      }</a></p>`,
    });

    const fallbackEventListener = () => {
      trackV3('click_button', {
        button: {
          name: 'ent_website_embedded_pdf_download',
        },
      });
      track({
        trackingName: 'embedded_pdf_fallback_url',
        trackingData: {
          url,
        },
        action: 'download_click',
      });
    };

    if (el) {
      trackV3('view_embedded_pdf', {
        embeddedPdf: {
          pdfUrl: url,
          pdfRenderStatus: 'success',
        },
      });
      track({
        trackingName: 'embedded_pdf',
        trackingData: {
          url,
        },
        action: 'render',
      });
    } else {
      trackV3('view_embedded_pdf', {
        embeddedPdf: {
          pdfUrl: url,
          pdfRenderStatus: 'failure',
        },
      });
      track({
        trackingName: 'embedded_pdf',
        trackingData: {
          url,
        },
        action: 'render_fallback',
      });
      document.querySelector('#pdf-object .download-link')?.addEventListener('click', fallbackEventListener);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      document.querySelector('#pdf-object .download-link')?.removeEventListener('click', fallbackEventListener);
    };
  }, [url, reload, track, trackV3]);

  return (
    <>
      <Helmet>
        <script
          src="https://cdnjs.cloudflare.com/ajax/libs/pdfobject/2.2.12/pdfobject.min.js"
          integrity="sha512-lDL6DD6x4foKuSTkRUKIMQJAoisDeojVPXknggl4fZWMr2/M/hMiKLs6sqUvxP/T2zXdrDMbLJ0/ru8QSZrnoQ=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      </Helmet>
      <div css={styles.root}>
        <div id="pdf-object" css={styles.pdfContainer} />
      </div>
    </>
  );
}

export default PDFObjectRenderer;
